import { MapPin, Mail } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useShopFlow } from "../constants/ShopFlow";
import { paymentMethod, quickLink, socmed } from "@/constants/footer";
import { useTranslation } from "react-i18next";
import { useContext } from "react";
import { AppContext } from "@/contexts/app-context";

export default function Footer() {
    const { app } = useContext(AppContext);
    const { lang } = useParams();
    const { t } = useTranslation();
    const currentLang = lang || "id";
    const isShopFlow = useShopFlow();

    return (
        <footer
            className={`${isShopFlow ? "hidden md:block" : "block"} bg-orange-50 dark:bg-[#02121f] border-t-4 border-gold-light dark:border-gold pt-10 md:pt-12 pb-6 px-6 transition-colors duration-300 relative overflow-hidden`}>
            <div className="absolute top-0 left-0 w-full h-px bg-linear-to-r from-transparent via-gold-light to-transparent opacity-50" />
            <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-6 mb-6 relative z-10">
                <div className="col-span-1 md:col-span-1">
                    <div className="flex flex-col md:flex-row items-center gap-0 md:gap-2 font-serif text-2xl font-bold mb-2">
                        <img
                            src="/assets/icons/logo-2.webp"
                            alt=""
                            className="h-20 md:h-16 w-auto object-contain mb-2"
                        />
                    </div>
                    <p className="text-dark/70 dark:text-white/60 text-sm text-center md:text-left leading-relaxed mb-4 md:mb-6">
                        {t("footer.desc")}
                    </p>
                    <div className="flex gap-4 justify-center md:justify-start">
                        {socmed(
                            app?.contact.whatsapp || "",
                            app?.contact.instagram || "",
                            app?.contact.tiktok || "",
                        ).map((s, i) => (
                            <a
                                key={`socmed-${i}`}
                                href={s.link}
                                target="_blank"
                                rel="noreferrer"
                                className="relative group w-10 h-10 rounded-full bg-white dark:bg-dark border border-gold/30 flex items-center justify-center text-gold hover:bg-gold hover:text-white transition-colors shadow-sm"
                                aria-label={s.name}>
                                {typeof s.svg === "string" ? (
                                    <img
                                        src={s.svg}
                                        alt={s.name}
                                        className={`${s.name === "WhatsApp" ? "w-5.5 h-5.5" : "w-5 h-5"} transition-all`}
                                    />
                                ) : (
                                    <s.svg
                                        className={`${s.name === "WhatsApp" ? "w-5.5 h-5.5" : "w-5 h-5"} fill-current transition-all`}
                                    />
                                )}
                                <span className="absolute -top-10 scale-0 opacity-0 transition-all duration-200 rounded-md bg-dark dark:bg-white px-2.5 py-1.5 text-xs text-white dark:text-dark font-medium shadow-md whitespace-nowrap group-hover:scale-100 group-hover:opacity-100 origin-bottom pointer-events-none">
                                    {s.name}
                                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 border-4 border-transparent border-t-dark dark:border-t-white"></span>
                                </span>
                            </a>
                        ))}
                    </div>
                </div>

                <div className="px-0 md:px-10">
                    <h3 className="font-serif font-bold text-lg text-dark dark:text-white mb-6">
                        {t("footer.quick_link.label")}
                    </h3>
                    <ul className="space-y-3 grid grid-cols-2 md:grid-cols-1 text-sm text-dark/70 dark:text-white/60">
                        {quickLink(t).map((q, i) => (
                            <li key={`quick-link-${i}`}>
                                <Link
                                    to={`/${currentLang}/${q.link}`}
                                    className="hover:text-gold transition-colors">
                                    {q.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Address */}
                <div>
                    <h3 className="font-serif font-bold text-lg text-dark dark:text-white mb-6">
                        {t("footer.address.label")}
                    </h3>
                    <div className="flex items-start gap-3 text-sm text-dark/70 dark:text-white/60 mb-4">
                        <MapPin
                            size={18}
                            className="text-gold shrink-0 mt-0.5"
                        />
                        <p className="leading-relaxed">
                            {t("footer.address.place")}
                            <br />
                            Jawa Timur, Indonesia
                        </p>
                    </div>
                    <div className="flex items-start gap-3 text-sm text-dark/70 dark:text-white/60 mb-4">
                        <Mail size={18} className="text-gold shrink-0 mt-0.5" />
                        <p className="leading-relaxed">{app?.contact.email}</p>
                    </div>
                </div>

                {/* Payment Methods */}
                <div>
                    <h3 className="font-serif font-bold text-lg text-dark dark:text-white mb-4">
                        {t("footer.payment_method_label")}
                    </h3>
                    <div className="flex flex-wrap items-center gap-4 bg-white/50 dark:bg-dark p-4 rounded-xl border border-gold/20 shadow-sm">
                        {paymentMethod.map((pay, i) => (
                            <div
                                key={`footer-payment-method-${i}`}
                                className="relative group flex items-center justify-center">
                                <img
                                    src={pay.src}
                                    alt=""
                                    loading="lazy"
                                    width="48"
                                    height="32"
                                    className={`${pay.height} w-auto object-contain drop-shadow-sm transition-transform group-hover:scale-110`}
                                />
                                <span className="absolute -top-10 scale-0 opacity-0 transition-all duration-200 rounded-md bg-dark dark:bg-white px-2.5 py-1 text-xs text-white dark:text-dark font-medium shadow-md whitespace-nowrap group-hover:scale-100 group-hover:opacity-100 origin-bottom pointer-events-none z-20">
                                    {pay.name}
                                    <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 border-4 border-transparent border-t-dark dark:border-t-white"></span>
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto pt-4 flex justify-center md:justify-start border-t border-gold/50 text-sm text-dark/70 dark:text-white/40">
                <p>
                    &copy; {new Date().getFullYear()} Omah Ring. All rights
                    reserved.
                </p>
            </div>
        </footer>
    );
}
